import { useState } from "react";
import type { Transaction } from "../../types";
import { advanceDueDate, dateLabel, isIncome, money } from "../../lib/finance";
import { formatMoney } from "../../lib/money-math";
import Modal from "../../components/Modal";
import DateInput from "../../components/DateInput";
import FormActions from "../../components/FormActions";
import { api } from "../../lib/api";

/** The shortcut from a transaction's own detail view: turn it into a bill without a trip to Bills.
 * Account/category/amount/payee come straight from the transaction — only what a bill needs that
 * the transaction doesn't already have (name, frequency, reminder) is asked here. Uses the same
 * sourceTransactionId anchor as the Bills screen's own "base this on an existing transaction" picker,
 * so this transaction becomes the bill's first paid cycle rather than a new one being created. */
export default function MakeTransactionRecurring({
  transaction,
  onClose,
  onCreated,
}: {
  transaction: Transaction;
  onClose: () => void;
  onCreated: () => Promise<unknown>;
}) {
  const income = isIncome(transaction);
  const [name, setName] = useState(transaction.title || transaction.description || "");
  const [frequency, setFrequency] = useState<"weekly" | "monthly" | "yearly">("monthly");
  const [reminderDaysBefore, setReminderDaysBefore] = useState("");
  const [nextDue, setNextDue] = useState("");
  const paidOn = transaction.date.slice(0, 10);
  const nextDueDate = nextDue || advanceDueDate(paidOn, frequency);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api("bills/create", {
        requestId: crypto.randomUUID(),
        sourceTransactionId: transaction.id,
        type: transaction.type === "Income" ? "Income" : "Expense",
        name,
        accountId: transaction.accountId,
        payeeId: transaction.payeeId,
        category: transaction.category,
        subcategory: transaction.subcategory,
        amount: transaction.amount ? formatMoney(transaction.amount) : "",
        currency: transaction.currency,
        frequency,
        nextDueDate,
        reminderDaysBefore,
      });
      await onCreated();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={income ? "Make this recurring income" : "Make this a recurring bill"} onClose={onClose}>
      {error && <p className="form-error" role="alert">{error}</p>}
      <form className="account-editor" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <p className="muted">
          {transaction.title || transaction.description} · {money(transaction.amount, transaction.currency)} · {dateLabel(transaction.date)}
        </p>
        <label>{income ? "Income name" : "Bill name"}<input required autoFocus name="bill-name" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} placeholder={income ? "e.g. Salary" : "e.g. Rent"} /></label>
        <label>
          Frequency
          <select value={frequency} onChange={(e) => { setFrequency(e.target.value as typeof frequency); setNextDue(""); }}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </label>
        <label>
          {income ? "Next expected" : "Next due"}
          <DateInput
            required
            min={new Date(Date.parse(`${paidOn}T00:00:00Z`) + 86400000).toISOString().slice(0, 10)}
            value={nextDueDate}
            onChange={(e) => setNextDue(e.target.value)}
          />
        </label>
        <label>
          <span className="field-label">Remind me <small>Days before, optional</small></span>
          <input
            inputMode="numeric"
            pattern="[0-9]+"
            value={reminderDaysBefore}
            onChange={(e) => setReminderDaysBefore(e.target.value)}
            placeholder="e.g. 3"
          />
        </label>
        <p className="muted">
          This transaction becomes the first {income ? "received" : "paid"} cycle. Change the date above if it normally falls on a different day.
        </p>
        <FormActions label="Make recurring" busy={busy} />
      </form>
    </Modal>
  );
}
